import {readDownloadErrorMessage} from "../../downloadFile";
import {
    buildPublicEventFormUrl,
    buildPublicRegistrationProofUploadUrl,
    buildPublicRegistrationResendConfirmationUrl,
    buildPublicRegistrationSubmitUrl
} from "./publicRegistrationModel";
import type {
    PublicEventFormResponse,
    PublicRegistrationResponse,
    PublicRegistrationSubmitPayload
} from "./types";

const postJson = async <T>(url: string, body: unknown, fallbackError: string): Promise<T> => {
    const response = await fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        throw new Error(await readDownloadErrorMessage(response, fallbackError));
    }

    return await response.json() as T;
};

export const fetchPublicEventForm = async (
    eventIdentifier: string,
    signal?: AbortSignal
): Promise<PublicEventFormResponse> => {
    const response = await fetch(buildPublicEventFormUrl(eventIdentifier), {
        headers: {
            Accept: "application/json",
        },
        signal,
    });

    if (!response.ok) {
        throw new Error(`Public form request failed with ${response.status}.`);
    }

    return await response.json() as PublicEventFormResponse;
};

export const uploadParticipantTypeProofFile = async (
    eventIdentifier: string,
    file: File,
    fallbackError: string
): Promise<{ id: number; fileName?: string | null }> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(buildPublicRegistrationProofUploadUrl(eventIdentifier), {
        method: "POST",
        headers: {
            Accept: "application/json"
        },
        body: formData
    });

    if (!response.ok) {
        throw new Error(await readDownloadErrorMessage(response, fallbackError));
    }

    return await response.json() as { id: number; fileName?: string | null };
};

export const submitPublicRegistration = (
    eventIdentifier: string,
    payload: PublicRegistrationSubmitPayload,
    fallbackError: string
) => postJson<PublicRegistrationResponse>(buildPublicRegistrationSubmitUrl(eventIdentifier), payload, fallbackError);

export const resendPublicRegistrationConfirmation = (
    eventIdentifier: string,
    registrationId: number,
    email: string,
    fallbackError: string
) => postJson<PublicRegistrationResponse>(
    buildPublicRegistrationResendConfirmationUrl(eventIdentifier, registrationId),
    {email},
    fallbackError);
